import { makeAutoObservable, runInAction } from "mobx";

import {
  collapsePropertyOfferLineages,
  isOptimisticDealId,
  propertyOfferRowsMatch,
  resolveDealDeallensToken,
  resolvePropertyOfferListId,
  transformDealToOffer,
} from "./propertyOfferUtils";
import { stripDeallensIdPrefix } from "./deallensIdUtils";
import { fetchPropertyOffers } from "./dealsApi";
import { fetchCollectionOffers, fetchCollectionTips } from "./collectionApi";
import {
  normalizeCollectionFeedDeals,
  resolveDisplayOffer,
  resolveExplicitOfferAmount,
} from "./collectionOfferResolvers";
import { DealLensStatus, normalizeDealLensStatus } from "./dealsConstants";

/** Keep recently tail-patched committed rows across lagging API refetches. */
const PRESERVE_PATCHED_MS = 2 * 60 * 1000;

/**
 * @param {object | null | undefined} source
 * @returns {number}
 */
function readNestedOfferAmount(source) {
  if (!source || typeof source !== "object") return 0;
  const direct = Number(source.offer_amount);
  if (Number.isFinite(direct) && direct > 0) return direct;
  let calculation = source.calculation;
  if (typeof calculation === "string") {
    try {
      calculation = JSON.parse(calculation);
    } catch {
      calculation = null;
    }
  }
  const fromCalc = Number(calculation?.offer_amount);
  if (Number.isFinite(fromCalc) && fromCalc > 0) return fromCalc;
  const metadata = source.metadata && typeof source.metadata === "object" ? source.metadata : null;
  const fromMeta = Number(metadata?.offer_amount ?? metadata?.investor_offer_amount);
  if (Number.isFinite(fromMeta) && fromMeta > 0) return fromMeta;
  return 0;
}

function explicitOfferAmount(row) {
  return readNestedOfferAmount(row);
}

/**
 * @param {object} existing
 * @param {object} incoming
 * @returns {object}
 */
function mergeIncomingPropertyOfferPatch(existing, incoming) {
  const incomingAmount = explicitOfferAmount(incoming);
  if (incomingAmount > 0) return incoming;

  const preservedAmount = explicitOfferAmount(existing);
  if (preservedAmount <= 0) return incoming;

  const status = normalizeDealLensStatus(incoming?.status);
  if (status === DealLensStatus.ACCEPTED || status === DealLensStatus.WITHDRAWN) {
    return {
      ...incoming,
      calculation: { ...(incoming.calculation ?? {}), offer_amount: preservedAmount },
    };
  }

  return incoming;
}

/**
 * @param {string | null | undefined} propertyId
 * @returns {string}
 */
function normalizePropertyCacheId(propertyId) {
  return String(propertyId ?? "")
    .trim()
    .replace(/^property#/i, "");
}

/**
 * @param {string | null | undefined} role
 * @returns {string}
 */
function normalizeRole(role) {
  return String(role ?? "")
    .trim()
    .toLowerCase();
}

const OTHER_ROLE = { investor: "realtor", realtor: "investor" };

/**
 * OPEN/SHARE rows without a submitted offer must not override a MESSAGE tip on hub reload.
 *
 * @param {object | null} offer
 * @returns {boolean}
 */
function latestOfferIsDraftShare(offer) {
  if (!offer || typeof offer !== "object") return false;
  const status = String(offer.offer_status ?? "").toUpperCase();
  const event = String(offer.offer_event ?? "").toUpperCase();
  const amount = Number(offer.offer_amount);
  const hasPositiveAmount = Number.isFinite(amount) && amount > 0;
  if (status === "OPEN" && event === "SHARE") return true;
  if (status === "OPEN" && !hasPositiveAmount) return true;
  return false;
}

/**
 * @param {object | null | undefined} offer
 * @returns {boolean}
 */
function isSubmittedCollectionOffer(offer) {
  if (!offer || typeof offer !== "object") return false;
  if (latestOfferIsDraftShare(offer)) return false;
  const event = String(offer.offer_event ?? "").toUpperCase();
  if (event === "ACCEPT" || event === "WITHDRAW") return true;
  const status = String(offer.offer_status ?? offer.status ?? "").toUpperCase();
  const amount = Number(offer.offer_amount ?? offer.calculation?.offer_amount);
  if (!Number.isFinite(amount) || amount <= 0) return false;
  return (
    status === DealLensStatus.OPEN ||
    status === DealLensStatus.COUNTERED ||
    status === DealLensStatus.ACCEPTED
  );
}

/**
 * @param {object} tipRow
 * @param {object} tip
 * @param {object} metadata
 * @returns {object | null}
 */
function resolveLatestOfferSnapshot(tipRow, tip, metadata) {
  const fromRow =
    tipRow?.latest_offer && typeof tipRow.latest_offer === "object" ? tipRow.latest_offer : null;
  if (fromRow && isSubmittedCollectionOffer(fromRow)) return fromRow;
  if (fromRow && !latestOfferIsDraftShare(fromRow)) return fromRow;

  const metaAmount = Number(metadata?.offer_amount);
  const metaStatus = String(metadata?.offer_status ?? "").toUpperCase();
  if (
    Number.isFinite(metaAmount) &&
    metaAmount > 0 &&
    isSubmittedCollectionOffer({ offer_amount: metaAmount, offer_status: metaStatus })
  ) {
    return { offer_amount: metaAmount, offer_status: metaStatus };
  }

  const tipAmount = Number(tip?.offer_amount);
  const tipStatus = String(tip?.offer_status ?? "").toUpperCase();
  if (
    Number.isFinite(tipAmount) &&
    tipAmount > 0 &&
    isSubmittedCollectionOffer({ offer_amount: tipAmount, offer_status: tipStatus })
  ) {
    return {
      offer_amount: tipAmount,
      offer_status: tipStatus,
      offer_id: tip?.offer_id,
      deallens_id: tip?.deallens_id,
      direction: tip?.direction,
      sender: tip?.sender,
      receiver: tip?.receiver,
      offer_event: tip?.offer_event,
    };
  }

  return fromRow;
}

/**
 * @param {object} tipRow
 * @param {string} [viewerRole] - Role the tips were fetched for; OFFER tips carry no `role`, only viewer-relative `direction`.
 * @returns {object}
 */
export function mapTipToRawDeal(tipRow, viewerRole = "") {
  const tip = tipRow?.tip && typeof tipRow.tip === "object" ? tipRow.tip : {};
  const metadata =
    tip?.metadata && typeof tip.metadata === "object" && !Array.isArray(tip.metadata)
      ? tip.metadata
      : {};
  const latestOffer = resolveLatestOfferSnapshot(tipRow, tip, metadata);
  const messageText = typeof tip?.message === "string" ? tip.message.trim() : "";
  const isMessageTip = String(tip?.type ?? "").toUpperCase() === "MESSAGE";
  const messageTipDrivesListRow =
    isMessageTip &&
    messageText &&
    !isSubmittedCollectionOffer(latestOffer) &&
    (!latestOffer || latestOfferIsDraftShare(latestOffer));
  const offerSource = messageTipDrivesListRow
    ? null
    : latestOffer ?? (String(tip?.type ?? "").toUpperCase() === "OFFER" ? tip : null);
  const rawAmount =
    readNestedOfferAmount(offerSource) ||
    readNestedOfferAmount(tip) ||
    readNestedOfferAmount(latestOffer) ||
    readNestedOfferAmount({ metadata }) ||
    null;
  const normalizedAmount = Number(rawAmount);
  const offerEvent = String(
    offerSource?.offer_event ?? tip?.offer_event ?? metadata.offer_event ?? ""
  ).toUpperCase();
  const offerStatus = String(
    offerSource?.offer_status ?? tip?.offer_status ?? metadata.offer_status ?? tip?.status ?? ""
  ).toUpperCase();
  const statusFromEvent =
    offerEvent === "ACCEPT"
      ? DealLensStatus.ACCEPTED
      : offerEvent === "WITHDRAW"
        ? DealLensStatus.WITHDRAWN
        : "";
  const status = messageTipDrivesListRow
    ? DealLensStatus.MESSAGE
    : normalizeDealLensStatus(statusFromEvent || offerStatus || DealLensStatus.MESSAGE);
  const deallensId = offerSource?.deallens_id ?? tip?.deallens_id ?? metadata.deallens_id ?? null;
  const propertyId = offerSource?.property_id ?? tip?.property_id ?? metadata.property_id ?? null;
  const role = normalizeRole(viewerRole);
  // Whose turn it is follows the latest OFFER's viewer-relative direction; tip is only latest activity.
  const direction = String(offerSource?.direction ?? tip?.direction ?? "").trim().toUpperCase();
  let senderRole = offerSource ? "" : normalizeRole(tip?.role ?? tip?.sender_role);
  if (!senderRole && OTHER_ROLE[role]) {
    if (direction === "OUTGOING") senderRole = role;
    if (direction === "INCOMING") senderRole = OTHER_ROLE[role];
  }
  let receiverRole = offerSource ? "" : String(tip?.receiver_role ?? "").trim().toLowerCase();
  if (!receiverRole && (senderRole === "investor" || senderRole === "realtor")) {
    receiverRole = senderRole === "investor" ? "realtor" : "investor";
  }

  const calculationPatch =
    !messageTipDrivesListRow && Number.isFinite(normalizedAmount) && normalizedAmount > 0
      ? { calculation: { offer_amount: normalizedAmount } }
      : status === DealLensStatus.ACCEPTED && latestOffer
        ? (() => {
            const acceptedAmount = Number(latestOffer.offer_amount ?? latestOffer.calculation?.offer_amount);
            return Number.isFinite(acceptedAmount) && acceptedAmount > 0
              ? { calculation: { offer_amount: acceptedAmount } }
              : {};
          })()
        : {};

  return {
    id: deallensId ?? tip?.offer_id ?? tip?.id ?? null,
    deallens_id: deallensId,
    property_id: propertyId,
    collection_id: tipRow?.collection_id ?? tip?.collection_id ?? tip?.thread ?? null,
    status,
    ...calculationPatch,
    offer_id: offerSource?.offer_id ?? tip?.offer_id ?? null,
    sender: offerSource?.sender ?? tip?.sender ?? "",
    receiver: offerSource?.receiver ?? tip?.receiver ?? "",
    sender_role: senderRole,
    receiver_role: receiverRole,
    insert_date: tip?.insert_date ?? tip?.last_modified ?? null,
    last_modified: tip?.last_modified ?? tip?.insert_date ?? null,
    metadata: {
      ...metadata,
      ...(messageText ? { message: messageText } : {}),
      ...(offerSource?.offer_event ? { offer_event: offerSource.offer_event } : {}),
      ...(tip?.offer_event ? { offer_event: tip.offer_event } : {}),
      ...(tipRow?.tip_status ? { tip_status: tipRow.tip_status } : {}),
      ...(latestOffer?.offer_id ? { latest_offer_id: latestOffer.offer_id } : {}),
    },
    raw: tip,
  };
}

/**
 * True when `candidate` is the direct lineage parent of `tip`
 * (`tip.metadata.previous_deallens_id` → candidate).
 *
 * @param {object | null | undefined} candidate
 * @param {object | null | undefined} tip
 * @returns {boolean}
 */
function isLineagePredecessorOf(candidate, tip) {
  const tipPrevToken = stripDeallensIdPrefix(tip?.metadata?.previous_deallens_id ?? "");
  const candidateToken = resolveDealDeallensToken(candidate);
  return Boolean(tipPrevToken && candidateToken && tipPrevToken === candidateToken);
}

/**
 * @param {object[]} rawDeals
 * @param {object} incomingDeal
 * @param {{ allowPredecessorReplace?: boolean }} [options]
 * @returns {object[]}
 */
export function patchRawPropertyOfferDeals(
  rawDeals,
  incomingDeal,
  { allowPredecessorReplace = false } = {}
) {
  if (!incomingDeal || !normalizeDealLensStatus(incomingDeal.status)) {
    return Array.isArray(rawDeals) ? rawDeals : [];
  }

  const base = Array.isArray(rawDeals) ? rawDeals.slice() : [];
  const existingIndex = base.findIndex((row) => propertyOfferRowsMatch(row, incomingDeal));
  if (existingIndex >= 0) {
    const existing = base[existingIndex];
    // Lineage match treats tip ↔ parent as the same list row. Never regress a tip
    // (e.g. MESSAGE) back to its OPEN/COUNTERED predecessor after send/reconcile —
    // unless this is an intentional optimistic revert.
    if (!allowPredecessorReplace && isLineagePredecessorOf(incomingDeal, existing)) {
      return dedupePropertyOfferRawDeals(collapsePropertyOfferLineages(base));
    }
    base[existingIndex] = mergeIncomingPropertyOfferPatch(existing, incomingDeal);
  } else {
    base.unshift(incomingDeal);
  }

  return dedupePropertyOfferRawDeals(collapsePropertyOfferLineages(base));
}

/**
 * @param {object[]} deals
 * @returns {object[]}
 */
function dedupePropertyOfferRawDeals(deals) {
  return deals.filter((deal, index, arr) => {
    const key = resolvePropertyOfferListId(deal) ?? "";
    if (!key) return true;
    return arr.findIndex((row) => resolvePropertyOfferListId(row) === key) === index;
  });
}

/**
 * @param {object | null | undefined} deal
 * @returns {number}
 */
function dealInsertTimestamp(deal) {
  const time = new Date(deal?.insert_date ?? deal?.last_modified ?? "").getTime();
  return Number.isFinite(time) ? time : 0;
}

/**
 * @param {object | null | undefined} deal
 * @returns {boolean}
 */
function isMessageDealRow(deal) {
  return normalizeDealLensStatus(deal?.status) === DealLensStatus.MESSAGE;
}

/**
 * Prefer a cached row over a stale API row for the same collection list id.
 *
 * @param {object} cached
 * @param {object} incoming
 * @returns {boolean}
 */
function shouldPreferCachedCollectionRow(cached, incoming) {
  if (isMessageDealRow(cached) && !isMessageDealRow(incoming)) return true;

  const incomingStatus = normalizeDealLensStatus(incoming?.status);
  if (
    incomingStatus === DealLensStatus.ACCEPTED &&
    explicitOfferAmount(incoming) <= 0 &&
    explicitOfferAmount(cached) > 0 &&
    normalizeDealLensStatus(cached?.status) === DealLensStatus.ACCEPTED
  ) {
    return true;
  }

  // API lag: property-threads may omit collection_id briefly after a write.
  const cachedHasCollection = Boolean(String(cached?.collection_id ?? "").trim());
  const incomingHasCollection = Boolean(String(incoming?.collection_id ?? "").trim());
  if (cachedHasCollection && !incomingHasCollection) return true;

  const cachedPrevToken = stripDeallensIdPrefix(cached?.metadata?.previous_deallens_id ?? "");
  const incomingToken = resolveDealDeallensToken(incoming);
  if (cachedPrevToken && incomingToken && cachedPrevToken === incomingToken) return true;

  return dealInsertTimestamp(cached) > dealInsertTimestamp(incoming);
}

/**
 * Keep patched rows the API has not returned yet (MESSAGE tips, optimistic ids,
 * or rows recently written via patchFromTail while getDeallensPropertyThreads lags).
 *
 * @param {object} cached
 * @param {object[]} incomingDeals
 * @param {Map<string, number> | null | undefined} [patchedAtByListId]
 * @param {number} [now]
 * @returns {boolean}
 */
function shouldPreserveCachedRow(cached, incomingDeals, patchedAtByListId = null, now = Date.now()) {
  const listId = resolvePropertyOfferListId(cached);
  if (!listId) return false;
  if (incomingDeals.some((row) => propertyOfferRowsMatch(row, cached))) return false;
  if (isMessageDealRow(cached) || isOptimisticDealId(cached)) return true;
  const patchedAt = patchedAtByListId?.get?.(listId);
  return Number.isFinite(patchedAt) && now - patchedAt < PRESERVE_PATCHED_MS;
}

/**
 * Merges a property-offer refetch with the existing cache so optimistic/patched tips
 * are not wiped when the API response lags behind a send-message mutation.
 *
 * @param {object[]} prevDeals
 * @param {object[]} incomingDeals
 * @param {{ patchedAtByListId?: Map<string, number> | null }} [options]
 * @returns {object[]}
 */
export function mergeFetchedPropertyOfferDeals(prevDeals, incomingDeals, { patchedAtByListId = null } = {}) {
  const incoming = Array.isArray(incomingDeals) ? incomingDeals : [];
  const prev = Array.isArray(prevDeals) ? prevDeals : [];
  if (prev.length === 0) return incoming;

  let merged = incoming.slice();

  for (const cached of prev) {
    const matchIndex = merged.findIndex((row) => propertyOfferRowsMatch(row, cached));
    if (matchIndex >= 0) {
      const incomingRow = merged[matchIndex];
      merged[matchIndex] = shouldPreferCachedCollectionRow(cached, incomingRow)
        ? cached
        : mergeIncomingPropertyOfferPatch(cached, incomingRow);
      continue;
    }
    if (shouldPreserveCachedRow(cached, incoming, patchedAtByListId)) {
      merged.push(cached);
    }
  }

  return dedupePropertyOfferRawDeals(collapsePropertyOfferLineages(merged));
}

/**
 * Accepted tips often omit offer_amount. Copy it from the collection's last priced offer.
 *
 * @param {object[]} rawDeals
 * @param {{ customer?: string, propertyId?: string, role?: string }} [context]
 * @returns {Promise<object[]>}
 */
export async function hydrateTerminalOfferAmounts(rawDeals, { customer, propertyId, role } = {}) {
  const rows = Array.isArray(rawDeals) ? rawDeals : [];
  const pending = rows.filter((row) => {
    const status = normalizeDealLensStatus(row?.status);
    if (status !== DealLensStatus.ACCEPTED) return false;
    if (explicitOfferAmount(row) > 0) return false;
    return Boolean(String(row?.collection_id ?? "").trim());
  });
  if (pending.length === 0) return rows;

  await Promise.all(
    pending.map(async (row) => {
      try {
        const result = await fetchCollectionOffers({
          customer,
          propertyId: row.property_id || propertyId,
          collectionId: row.collection_id,
          ...(role ? { role } : {}),
        });
        const feed = normalizeCollectionFeedDeals(Array.isArray(result?.data) ? result.data : []);
        const amount = resolveExplicitOfferAmount(resolveDisplayOffer(feed));
        if (amount > 0) {
          row.calculation = { ...(row.calculation ?? {}), offer_amount: amount };
        }
      } catch {
        // Leave the accepted row; the hub still shows the status pill.
      }
    })
  );
  return rows;
}

/**
 * Per-property offer rows fetched from getDeallensPropertyThreads.
 *
 * @param {{ _bumpPropertyOfferRevision?: Function }} host
 */
class PropertyOfferCache {
  /**
   * @type {Map<string, {
   *   rawDeals: object[],
   *   fetchedAt: number|null,
   *   loading: boolean,
   *   error: string|null,
   *   byCollection: boolean,
   *   patchedAtByListId: Map<string, number>,
   * }>}
   */
  _entries = new Map();
  _inFlight = new Map();

  constructor(host) {
    /** @type {typeof host} */
    this._host = host;
    makeAutoObservable(this, { _host: false, _inFlight: false }, { autoBind: true });
  }

  clear() {
    this._entries.clear();
    this._inFlight.clear();
  }

  clearForCustomer(customer) {
    const prefix = `${String(customer ?? "").trim()}::`;
    if (!prefix || prefix === "::") return;
    for (const key of this._entries.keys()) {
      if (key.startsWith(prefix)) this._entries.delete(key);
    }
    for (const key of this._inFlight.keys()) {
      if (key.startsWith(prefix)) this._inFlight.delete(key);
    }
  }

  /**
   * @param {string} customer
   * @param {string} propertyId
   * @param {boolean} [byCollection]
   * @param {string} [role]
   * @returns {string}
   */
  cacheKey(customer, propertyId, byCollection = false, role = "") {
    const normProperty = normalizePropertyCacheId(propertyId);
    const normRole = normalizeRole(role);
    return `${String(customer ?? "").trim()}::${normProperty}::${normRole}::${byCollection ? "coll" : "thread"}`;
  }

  /**
   * @param {string} customer
   * @param {string} propertyId
   * @param {boolean} [byCollection]
   * @param {string} [role]
   * @returns {{ rawDeals: object[], fetchedAt: number|null, loading: boolean, error: string|null, byCollection: boolean } | null}
   */
  getEntry(customer, propertyId, byCollection = false, role = "") {
    const key = this.cacheKey(customer, propertyId, byCollection, role);
    return this._entries.get(key) ?? null;
  }

  /**
   * @param {string} customer
   * @param {string} propertyId
   * @param {object[]} rawDeals
   * @param {{ byCollection?: boolean, role?: string }} [options]
   */
  ingest(customer, propertyId, rawDeals, { byCollection = false, role = "" } = {}) {
    const key = this.cacheKey(customer, propertyId, byCollection, role);
    let mergedDeals = [];
    runInAction(() => {
      const prev = this._entries.get(key);
      const incoming = Array.isArray(rawDeals) ? rawDeals : [];
      const patchedAtByListId = prev?.patchedAtByListId ?? new Map();
      const merged = mergeFetchedPropertyOfferDeals(prev?.rawDeals ?? [], incoming, {
        patchedAtByListId,
      });
      mergedDeals = merged;
      this._entries.set(key, {
        rawDeals: merged,
        fetchedAt: Date.now(),
        loading: false,
        error: null,
        byCollection,
        patchedAtByListId,
      });
      this._host._bumpPropertyOfferRevision?.();
    });
    this._host.promotePendingCollectionTailFromOffers?.(mergedDeals);
  }

  /**
   * Sync a deal update into cached property-offer rows (matched by collection_id list id).
   *
   * @param {object} tailDeal
   * @param {{ allowPredecessorReplace?: boolean }} [options]
   */
  patchFromTail(tailDeal, { allowPredecessorReplace = false } = {}) {
    const propertyToken = normalizePropertyCacheId(tailDeal?.property_id);
    const status = normalizeDealLensStatus(tailDeal?.status);
    // Drafts never appear in Offers & Conversations; patching one in would replace the thread's offer row.
    if (!propertyToken || !status || status === DealLensStatus.DRAFT) return;

    runInAction(() => {
      let patched = false;
      const listId = resolvePropertyOfferListId(tailDeal);
      const now = Date.now();
      for (const [key, entry] of this._entries.entries()) {
        const keyParts = key.split("::");
        if (keyParts.length < 4 || keyParts[1] !== propertyToken) continue;
        const nextDeals = patchRawPropertyOfferDeals(entry.rawDeals, tailDeal, {
          allowPredecessorReplace,
        });
        const patchedAtByListId = new Map(entry.patchedAtByListId ?? []);
        if (listId) patchedAtByListId.set(listId, now);
        this._entries.set(key, { ...entry, rawDeals: nextDeals, patchedAtByListId });
        patched = true;
      }
      if (patched) this._host._bumpPropertyOfferRevision?.();
    });
  }

  /**
   * @param {object} params
   * @param {string} params.customer
   * @param {string} params.propertyId
   * @param {boolean} [params.byCollection]
   * @param {string} [params.role]
   * @returns {Promise<object[]>}
   */
  async load({ customer, propertyId, byCollection = false, role = "" }) {
    const normRole = normalizeRole(role);
    const key = this.cacheKey(customer, propertyId, byCollection, normRole);
    const existing = this._inFlight.get(key);
    if (existing) return existing;

    runInAction(() => {
      const prev = this._entries.get(key);
      this._entries.set(key, {
        rawDeals: prev?.rawDeals ?? [],
        fetchedAt: prev?.fetchedAt ?? null,
        loading: true,
        error: null,
        byCollection,
        patchedAtByListId: prev?.patchedAtByListId ?? new Map(),
      });
      this._host._bumpPropertyOfferRevision?.();
    });

    const promise = (byCollection
      ? fetchCollectionTips({
          customer,
          propertyId,
          ...(normRole ? { role: normRole } : {}),
        }).then((result) => {
          const items = Array.isArray(result?.body?.items) ? result.body.items : [];
          return items.map((item) => mapTipToRawDeal(item, normRole));
        })
      : fetchPropertyOffers({ customer, propertyId, byCollection, role: normRole }))
      .then(async (rawDeals) => {
        const rows = Array.isArray(rawDeals) ? rawDeals : [];
        // Publish tips before amount hydration so list badges (e.g. Accepted) are not blocked on extra fetches.
        this.ingest(customer, propertyId, rows, { byCollection, role: normRole });
        const hydrated = await hydrateTerminalOfferAmounts(rows, {
          customer,
          propertyId,
          role: normRole,
        });
        this.ingest(customer, propertyId, hydrated, { byCollection, role: normRole });
        return hydrated;
      })
      .catch((err) => {
        runInAction(() => {
          const prev = this._entries.get(key);
          this._entries.set(key, {
            rawDeals: prev?.rawDeals ?? [],
            fetchedAt: prev?.fetchedAt ?? null,
            loading: false,
            error: "Failed to load offers",
            byCollection,
            patchedAtByListId: prev?.patchedAtByListId ?? new Map(),
          });
          this._host._bumpPropertyOfferRevision?.();
        });
        throw err;
      })
      .finally(() => {
        this._inFlight.delete(key);
      });

    this._inFlight.set(key, promise);
    return promise;
  }

  /**
   * @param {object} params
   * @param {string} params.customer
   * @param {string} params.propertyId
   * @param {boolean} [params.byCollection]
   * @param {string} [params.role]
   * @param {object} [params.viewerContext]
   * @param {Set<string>} [params.readOfferIds]
   * @returns {object[]}
   */
  getOffers({
    customer,
    propertyId,
    byCollection = false,
    role = "",
    viewerContext = {},
    readOfferIds = null,
  } = {}) {
    void this._host.propertyOfferRevision;

    const resolvedRole = normalizeRole(role || viewerContext?.currentRole);
    const entry = this.getEntry(customer, propertyId, byCollection, resolvedRole);
    const rawDeals = entry?.rawDeals ?? [];
    const readIdsSet = readOfferIds instanceof Set ? readOfferIds : new Set();

    return collapsePropertyOfferLineages(rawDeals)
      .filter((deal, index, arr) => {
        const listId = resolvePropertyOfferListId(deal) ?? "";
        if (!listId) return true;
        return arr.findIndex((row) => resolvePropertyOfferListId(row) === listId) === index;
      })
      .map((deal, index) => {
        const offer = transformDealToOffer(deal, index, viewerContext);
        if (offer.isNew && readIdsSet.has(String(offer.id))) {
          return { ...offer, isNew: false };
        }
        return offer;
      });
  }

  /**
   * @param {string} customer
   * @param {string} propertyId
   * @param {string} listId
   * @returns {object | null}
   */
  findRawDeal(customer, propertyId, listId, role = undefined) {
    const normListId = String(listId ?? "").trim();
    const normProperty = normalizePropertyCacheId(propertyId);
    const normCustomer = String(customer ?? "").trim();
    if (!normListId || !normProperty || !normCustomer) return null;

    if (role !== undefined && role !== null) {
      const normRole = normalizeRole(role);
      for (const byCollection of [false, true]) {
        const entry = this.getEntry(customer, propertyId, byCollection, normRole);
        for (const deal of entry?.rawDeals ?? []) {
          if (resolvePropertyOfferListId(deal) === normListId) return deal;
        }
      }
      return null;
    }

    const propertyPrefix = `${normCustomer}::${normProperty}::`;
    for (const [key, entry] of this._entries.entries()) {
      if (!key.startsWith(propertyPrefix)) continue;
      const keyParts = key.split("::");
      if (keyParts.length < 4 || keyParts[1] !== normProperty) continue;
      for (const deal of entry?.rawDeals ?? []) {
        if (resolvePropertyOfferListId(deal) === normListId) return deal;
      }
    }
    return null;
  }
}

export default PropertyOfferCache;
